import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiRequest from "../../../utils/vendor/apiRequest";
import InputField from "../../ui/form/InputField";
import FileInputField from "../../ui/form/FileInputField";
import Button from "../../ui/utils/Button";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";

const CreateVendorCompliance = ({ closeModal, callback }) => {
  const [preview, setPreview] = useState(null);

  const { error, isLoading: loading, post, serverErrors } = usePost(
    apiRequest.createVendorCompliance
  );

  const formik = useFormik({
    initialValues: {
      vendorId: "",
      vendorManagerId: "",
      vendorName: "",
      location: "",
      corporateCode: "",
      licenseName: "",
      expiryDate: "",
      document: null,
      notes: "",
    },

    validationSchema: Yup.object({
      vendorName: Yup.string().required("Vendor name is required"),
      location: Yup.string().required("Location is required"),
      corporateCode: Yup.string().required("Corporate code is required"),
      licenseName: Yup.string().required("License name is required"),
      expiryDate: Yup.date().required("Expiry date is required"),
      document: Yup.mixed()
        .required("Document file is required")
        .test("fileType", "Invalid file type", (value) =>
          value
            ? ["image/jpeg", "image/png", "application/pdf"].includes(value.type)
            : false
        ),
    }),

    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();

      Object.entries(values).forEach(([key, val]) => {
        formData.append(key, val);
      });

      const response = await post({ data: { formData } });

      if (response?.response) {
        callback?.();
        resetForm();
        closeModal();
      }
    },
  });

  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Create Vendor Compliance</h2>
      <div className="border-b" />

      <form onSubmit={formik.handleSubmit} className="text-sm">
        {error && <Alert message={error} type="error" />}

        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">

          <InputField
            name="vendorName"
            label="Vendor Name"
            value={formik.values.vendorName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.vendorName && formik.errors.vendorName}
            serverError={serverErrors?.vendorName}
            placeholder="Enter vendor name"
          />

          <InputField
            name="location"
            label="Location"
            value={formik.values.location}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.location && formik.errors.location}
            serverError={serverErrors?.location}
            placeholder="City / State / Area"
          />

          <InputField
            name="corporateCode"
            label="Corporate Code"
            value={formik.values.corporateCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.corporateCode && formik.errors.corporateCode}
            serverError={serverErrors?.corporateCode}
            placeholder="Corporate code"
          />

          <InputField
            name="licenseName"
            label="License Name"
            value={formik.values.licenseName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.licenseName && formik.errors.licenseName}
            serverError={serverErrors?.licenseName}
            placeholder="License Type"
          />

          <InputField
            name="expiryDate"
            type="date"
            label="Expiry Date"
            value={formik.values.expiryDate}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.expiryDate && formik.errors.expiryDate}
            serverError={serverErrors?.expiryDate}
          />

          <InputField
            name="notes"
            label="Notes"
            value={formik.values.notes}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.notes && formik.errors.notes}
            serverError={serverErrors?.notes}
            placeholder="Additional notes (optional)"
            textarea
          />

          <div className="col-span-full">
            <FileInputField
              id="document"
              label="Document File"
              accept="image/*,application/pdf"
              error={formik.touched.document && formik.errors.document}
              onChange={(e) => {
                const file = e.currentTarget.files?.[0] || null;
                formik.setFieldValue("document", file);
                setPreview(file ? URL.createObjectURL(file) : null);
              }}
            />

            {preview && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">Preview:</p>
                <img
                  src={preview}
                  alt="Preview"
                  className="w-20 h-20 border rounded object-cover mt-1"
                />
              </div>
            )}
          </div>
        </div>

        <div className="border-b" />

        <div className="flex justify-end gap-2 p-4">
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            loadingText="Creating..."
            isLoading={loading}
          >
            Create
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateVendorCompliance;
